import { i18n } from 'boot/i18n';

export const REPORT_DELETE_PROPERTY = Symbol('REPORT_DELETE_PROPERTY');

export const PROPERTIES_NAMES = {
  THEME: 'theme',
  CHART_TITLE: 'chartTitle',
  Y_DISABLE_GRID_LINES: 'yDisableGridLines',
  X_DISABLE_GRID_LINES: 'xDisableGridLines',
  CHART_FOOTER_TEXT: 'chartFooterText',
  SHOW_FOOTER: 'showFooter',
  TARGET_TABLE_COLUMNS: 'targetTableColumns',
  TARGET_TABLE_SORT_BY: 'targetTableSortBy',
  SHOW_TARGET_GRAPH: 'showTargetGraph',
  SHOW_PROGRAM_GRAPH: 'showProgramGraph',
  SHOW_ASSESSMENT_GRAPH: 'showAssessmentGraph',
  CHART_HEADER_TEXT: 'chartHeaderText',
  SHOW_HEADER: 'showHeader',
  SHOW_TREATMENT_PLAN_HEADINGS: 'showTreatmentPlanHeadings',
  SHOW_TARGET_LIST: 'showTargetList',
  PAGE_HEADER: 'pageHeader',
  PAGE_FOOTER: 'pageFooter',
  DEFAULT_CHART_SETTINGS: 'defaultChartSettings',
  DEFAULT_ASSESSMENT_CHART_SETTINGS: 'assessmentTypeContent',
};

export const FIELD_PREFIXES = {
  DATAFIELD: 'df_',
  STATUS_CHANGE: 'statusChange_',
};

export const REPORT_STATUSES = {
  DRAFT: 'draft',
  IN_REVIEW: 'in-review',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
  INVALID: 'invalid',
};

export const ALLOW_HIDE_REPORT_STATUSES = [
  REPORT_STATUSES.DRAFT,
  REPORT_STATUSES.IN_REVIEW,
];

export const REPORT_SYNC_STATUSES = {
  OPEN: 'open', // default
  FROZEN: 'frozen',
  LOCKED: 'locked',
};

export const REPORT_STATUS_META = {
  [REPORT_STATUSES.DRAFT]: {
    color: 'grey',
    textColor: 'white',
    label: i18n.t('progress.reports.statuses.draft'),
    allowSyncStatusChange: true,
  },
  [REPORT_STATUSES.IN_REVIEW]: {
    color: 'warning',
    textColor: 'white',
    label: i18n.t('progress.reports.statuses.in-review'),
    allowSyncStatusChange: true,
  },
  [REPORT_STATUSES.PUBLISHED]: {
    color: 'positive',
    textColor: 'white',
    label: i18n.t('progress.reports.statuses.published'),
    allowSyncStatusChange: true,
  },
  [REPORT_STATUSES.ARCHIVED]: {
    color: 'negative',
    textColor: 'white',
    label: i18n.t('progress.reports.statuses.archived'),
    allowSyncStatusChange: false,
    allowEditing: false,
  },
  [REPORT_STATUSES.INVALID]: {
    color: 'negative',
    textColor: 'white',
    label: i18n.t('progress.reports.statuses.invalid'),
    allowSyncStatusChange: false,
    allowEditing: false,
  },
};

export const REPORT_SYNC_STATUS_META = {
  [REPORT_SYNC_STATUSES.OPEN]: {
    tooltip: i18n.t('progress.reports.syncStatusTooltips.open'),
    syncIcon: 'autorenew',
  },
  [REPORT_SYNC_STATUSES.FROZEN]: {
    tooltip: i18n.t('progress.reports.syncStatusTooltips.frozen'),
    syncIcon: 'sync_disabled',
  },
  [REPORT_SYNC_STATUSES.LOCKED]: {
    tooltip: i18n.t('progress.reports.syncStatusTooltips.locked'),
    syncIcon: 'lock',
  },
};

export const REPORT_CONTEXT_MENU_ITEM_TYPES = {
  ERROR: 'error',
  COMMENTS: 'comments',
  EDIT: 'edit',
  ASSESSMENT_ADMINISTRATION: 'assessment-administration',
  PROGRAM_VISIBILITY: 'program-visibility',
  ASSESSMENT_GRAPH_VISIBILITY: 'assessment-graph-visibility',
  SETTINGS: 'settings',
  REMOVE: 'remove',
};

export const REPORT_CONTEXT_MENU_ITEM_INFO = {
  [REPORT_CONTEXT_MENU_ITEM_TYPES.ERROR]: {
    labelKey: 'common.errors.generic',
    icon: 'warning',
    iconColor: 'negative',
    isPersistent: true,
    order: 0,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.COMMENTS]: {
    labelKey: 'progress.reports.contextMenu.comments',
    icon: 'comments',
    order: 10,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.EDIT]: {
    labelKey: 'progress.reports.contextMenu.edit',
    icon: 'edit',
    order: 15,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.ASSESSMENT_ADMINISTRATION]: {
    labelKey: 'progress.reports.contextMenu.assessmentAdministration',
    icon: 'list_alt',
    order: 20,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.PROGRAM_VISIBILITY]: {
    labelKey: 'progress.reports.contextMenu.showProgram',
    icon: 'visibility',
    order: 100,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.ASSESSMENT_GRAPH_VISIBILITY]: {
    labelKey: 'progress.reports.contextMenu.showAssessmentGraph',
    icon: 'visibility',
    order: 100,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.SETTINGS]: {
    labelKey: 'progress.reports.contextMenu.settings',
    icon: 'settings',
    order: 1000,
  },
  [REPORT_CONTEXT_MENU_ITEM_TYPES.REMOVE]: {
    labelKey: 'progress.reports.contextMenu.remove',
    icon: 'delete',
    iconColor: 'negative',
    order: 1010,
  },
};

const DEFAULT_HEADING_CONTENT = {
  heading: '',
  headingStyle: 'h1',
};

export const REPORT_ELEMENTS_CONTENT_TYPES = {
  heading: {
    icon: 'fas fa-heading',
    labelKey: 'progressReport.itemHeading',
    defaultContent: { ...DEFAULT_HEADING_CONTENT },
    hasHeading: true,
  },
  text: {
    icon: 'notes',
    labelKey: 'progressReport.itemText',
    defaultContent: {
      ...DEFAULT_HEADING_CONTENT,
      textContent: '',
    },
    hasHeading: true,
  },
  programGroup: {
    icon: 'archive',
    labelKey: 'progressReport.itemProgramGroup',
    defaultContent: {
      chartHeaderText: '{{program.programName}}',
      chartFooterText: '',
      showTreatmentPlanHeadings: true,
    },
    hasHeading: true,
  },
  insights: {
    icon: 'insights',
    labelKey: 'progressReport.itemChart',
    defaultContent: { ...DEFAULT_HEADING_CONTENT },
    hasHeading: true,
  },
  chart: { // obsolete - use 'insights' instead
    icon: 'insights',
    labelKey: 'progressReport.itemChart',
    defaultContent: { ...DEFAULT_HEADING_CONTENT },
    hasHeading: true,
  },
  assessmentType: {
    icon: 'psychology',
    labelKey: 'progressReport.itemAssessment',
    defaultContent: {
      ...DEFAULT_HEADING_CONTENT,
      textContent: '',
      chartFooterText: '',
      heading: '',
    },
    hasHeading: true,
    hasSubItems: true,
  },
  signature: {
    icon: 'fas fa-signature',
    labelKey: 'progressReport.itemSignature',
    defaultContent: {
      userName: '',
      credentials: '',
      signature: '',
      fromTemplate: true,
    },
    hasHeading: true,
  },
  pageBreak: {
    icon: 'insert_page_break',
    labelKey: 'progressReport.itemPageBreak',
    hasHeading: false,
  },
};

export const CONTENT_TYPES = {
  ASSESSMENT_TYPE: 'assessmentType',
  CHART: 'chart',
  HEADING: 'heading',
  PAGE_BREAK: 'pageBreak',
  PAGE_FOOTER: 'pageFooter',
  PAGE_HEADER: 'pageHeader',
  PROGRAM_GROUP: 'programGroup',
  PROGRAM_PROGRESS: 'programProgress',
  SIGNATURE: 'signature',
  TEXT: 'text',
};
